import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import { App } from './app/App';
import { createServices } from './app/services';
import { applyBranding, cachedBranding } from './lib/branding';
import { StorageFailure } from './app/StorageFailure';
import { removeLegacyMeCache } from './app/me';
import { onNeedRefresh, registerUpdater, setRegistration, watchForNewVersion } from './lib/pwa';
import { reloadOnceForNewVersion } from './app/PageErrorBoundary';

// Paint with the last known branding immediately (works offline).
applyBranding(cachedBranding());

// A file this (older) release needs is gone after a new deploy: reload once for
// the new release instead of failing (app/PageErrorBoundary.tsx).
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForNewVersion()) event.preventDefault();
});

const services = createServices();
const root = createRoot(document.getElementById('root')!);

try {
  await services.storage.init();
  removeLegacyMeCache();
  root.render(
    <StrictMode>
      <App services={services} />
    </StrictMode>,
  );
} catch (e) {
  // Encrypted storage could not open (ADR-0021). Never fall back to plaintext:
  // explain why the app can't start instead of showing a blank screen.
  const code = (e as { code?: string }).code;
  root.render(<StorageFailure code={code === 'ENCRYPTION_UNAVAILABLE' || code === 'DATA_INTEGRITY' ? code : 'UNKNOWN'} />);
}

// Service worker for offline start (PWA). A waiting update is applied unless a
// survey is open for capture — except when the app is below the minimum
// version, where the update-required screen applies it on any page (lib/pwa.ts).
if ('serviceWorker' in navigator && import.meta.env.PROD && !(window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh: () => onNeedRefresh(),
      onRegisteredSW: (_url, reg) => setRegistration(reg),
    });
    registerUpdater(updateSW);
  });
  watchForNewVersion(navigator.serviceWorker);
}
